import type { ButtonHTMLAttributes, FC, PropsWithChildren, ReactNode } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { Button, type ButtonProps } from "./Button";
import { Icon, type Icons } from "./Icon";
import { Span } from "./Span";

export const calloutVariants = tv({
	slots: {
		base: "flex cursor-default flex-col items-center gap-4 rounded-lg border-l-4 px-5 py-3 shadow-xs lg:flex-row",
		icon: "flex-shrink-0",
		textContainer: "flex flex-1 flex-col gap-1",
		title: "font-bold",
		text: "text-default",
		action: "flex-shrink-0",
		importantText: "font-semibold",
	},
	variants: {
		color: {
			primary: {
				icon: "text-on-primary-light",
				base: "border-primary bg-primary-light",
				title: "text-on-primary-light",
				importantText: "text-on-primary-light",
			},
			danger: {
				icon: "text-on-danger-light",
				base: "border-danger bg-danger-light",
				title: "text-on-danger-light",
				importantText: "text-on-danger-light",
			},
			info: {
				icon: "text-on-info-light",
				base: "border-info bg-info-light",
				title: "text-on-info-light",
				importantText: "text-on-info-light",
			},
			success: {
				icon: "text-on-success-light",
				base: "border-success bg-success-light",
				title: "text-on-success-light",
				importantText: "text-on-success-light",
			},
			warning: {
				icon: "text-on-warning-light",
				base: "border-warning bg-warning-light",
				title: "text-on-warning-light",
				importantText: "text-on-warning-light",
			},
		},
	},
	defaultVariants: {
		color: "primary",
	},
});

type CalloutVariant = VariantProps<typeof calloutVariants>;

export type CalloutProps = CalloutVariant &
	Omit<ButtonHTMLAttributes<HTMLDivElement>, "children"> & {
		readonly icon?: Icons;
		readonly action?: Pick<
			ButtonProps,
			"leftIcon" | "rightIcon" | "onClick" | "variant" | "linkProps"
		> & {
			text?: string;
		};
		readonly title?: string;
		readonly children: ReactNode | ((ImportantText: FC<PropsWithChildren>) => ReactNode);
		readonly rightComponent?: ReactNode;
	};

export function Callout({
	icon,
	action,
	children,
	className,
	color,
	title,
	rightComponent,
	...divProps
}: CalloutProps) {
	const {
		icon: iconClassName,
		base: baseClassName,
		textContainer: textContainerClassName,
		title: titleClassName,
		text: textClassName,
		action: actionClassName,
		importantText: importantTextClassName,
	} = calloutVariants({ color });

	function ImportantText({ children }: PropsWithChildren) {
		return (
			<Span variant="body-sm" data-slot="importantText" className={importantTextClassName()}>
				{children}
			</Span>
		);
	}

	return (
		<div
			className={baseClassName({
				className: cn(className, divProps.onClick && "cursor-pointer"),
			})}
			{...divProps}
		>
			{icon && <Icon name={icon} size="xl" className={iconClassName()} />}
			<span className={textContainerClassName()}>
				{title && (
					<Span variant="title-xs" className={titleClassName()}>
						{title}
					</Span>
				)}
				{children && (
					<Span variant="body-sm" className={textClassName()}>
						{typeof children === "function" ? children(ImportantText) : children}
					</Span>
				)}
			</span>
			{action && (
				<Button {...action} size="sm" className={actionClassName()} color={color}>
					{action.text}
				</Button>
			)}
			{rightComponent}
		</div>
	);
}
